import { Component, LLMProvider, T_RESPONSE, CollectionRegistry, Tool, DashCompModelConfig } from "../types";
import { logger } from "../utils/logger";
import { LLM } from "../llm";
import { promptLoader } from "../userResponse/prompt-loader";
import { schema } from "../userResponse/schema";
import { userPromptErrorLogger } from "../utils/user-prompt-error-logger";
import { getApiKeyAndModel, formatComponentsForPrompt, formatToolsForPrompt, loadDatabaseRulesForTools, RESOLVE_QUERY_ID_TOOL, resolveQueryIdsTool } from "./utils";
import { getCurrentDateTimeForPrompt } from "../utils/datetime";
import { QueryExecutionService } from "../userResponse/services/query-execution-service";
import { ensureQueryLimit, validateAndFixSqlQuery, getDbTypeFromToolId } from "../userResponse/utils";
import { MAX_COMPONENT_QUERY_LIMIT, DEFAULT_QUERY_LIMIT, MAX_QUERY_VALIDATION_RETRIES, KNOWLEDGE_BASE_TOP_K } from "../userResponse/constants";
import { queryCache } from "../utils/query-cache";
import { buildDirectToolCacheKey } from "../utils/cache-key";
import KB from "../userResponse/knowledge-base";
import { extractPromptText } from "../userResponse/prompt-extractor";

/**
 * Pick a component and generate props using LLM
 * Supports both database queries and external tools for data viewing/modification
 */
export async function pickComponentWithLLM(
	prompt: string,
	components: Component[],
	anthropicApiKey?: string,
	groqApiKey?: string,
	geminiApiKey?: string,
	openaiApiKey?: string,
	llmProviders?: LLMProvider[],
	collections?: CollectionRegistry,
	tools?: Tool[],
	dashCompModels?: DashCompModelConfig,
	conversationHistory?: string,
	userId?: string
): Promise<T_RESPONSE> {
	const errors: string[] = [];

	// Format available components for the prompt
	const availableComponentsText = formatComponentsForPrompt(components);

	// Format available external tools for the prompt
	const availableToolsText = formatToolsForPrompt(tools);

	try {
		// Check if any tool is a database source tool (has 'sql' parameter)
		const hasDatabaseSourceTool = tools?.some(tool => {
			const params = tool.params || {};
			return 'sql' in params || Object.keys(params).some(key => key.toLowerCase() === 'sql');
		}) ?? false;

		// Get database schema documentation - skip if database source tools are available
		let schemaDoc: string;
		if (hasDatabaseSourceTool) {
			schemaDoc = 'Schema is available in the database source tool descriptions.';
		} else {
			schemaDoc = schema.generateSchemaDocumentation();
		}

		// Get database-specific SQL rules, scoped to the dialects actually present in `tools`
		const databaseRules = await loadDatabaseRulesForTools(tools);

		// Knowledge base context
		let globalKnowledgeBase = 'No global knowledge base available.';
		let knowledgeBaseContext = 'No additional knowledge base context available.';
		if (collections) {
			const kbResult = await KB.getAllKnowledgeBase({
				prompt,
				collections,
				userId,
				topK: KNOWLEDGE_BASE_TOP_K,
			});
			globalKnowledgeBase = kbResult.globalContext || globalKnowledgeBase;
			const dynamicParts: string[] = [];
			if (kbResult.userContext) {
				dynamicParts.push('## User-Specific Knowledge Base\n' + kbResult.userContext);
			}
			if (kbResult.queryContext) {
				dynamicParts.push('## Relevant Knowledge Base (Query-Matched)\n' + kbResult.queryContext);
			}
			knowledgeBaseContext = dynamicParts.join('\n\n') || knowledgeBaseContext;
		}

		// Load prompts for dash-comp-picker
		const prompts = await promptLoader.loadPrompts('dash-comp-picker', {
			USER_PROMPT: prompt,
			AVAILABLE_COMPONENTS: availableComponentsText,
			SCHEMA_DOC: schemaDoc || 'No database schema available',
			DATABASE_RULES: databaseRules,
			AVAILABLE_TOOLS: availableToolsText,
			CURRENT_DATETIME: getCurrentDateTimeForPrompt(),
			CONVERSATION_HISTORY: conversationHistory || 'No previous conversation',
			GLOBAL_KNOWLEDGE_BASE: globalKnowledgeBase,
			KNOWLEDGE_BASE_CONTEXT: knowledgeBaseContext,
		});

		logger.logLLMPrompt('dashCompPicker', 'system', extractPromptText(prompts.system));
		logger.logLLMPrompt('dashCompPicker', 'user', prompts.user);
		logger.debug('[DASH_COMP_REQ] Loaded dash-comp-picker prompts with schema and tools');

		const { apiKey, model } = getApiKeyAndModel(
			anthropicApiKey, groqApiKey, geminiApiKey, openaiApiKey, llmProviders, dashCompModels
		);

		if (!apiKey || !model) {
			errors.push('No API key available for any LLM provider');
			return { success: false, errors };
		}

		logger.info(`[DASH_COMP_REQ] Using model: ${model}`);

		// Track executed tools for response
		const executedTools: Array<{ id: string; name: string; params: any; result: any; outputSchema?: any }> = [];

		// Build LLM tool definitions from external tools
		const llmTools = (tools || []).map(tool => {
			const properties: Record<string, any> = {};
			const required: string[] = [];

			Object.entries(tool.params || {}).forEach(([key, typeOrValue]) => {
				const valueStr = String(typeOrValue).toLowerCase();

				// Extract type from description like "string - SQL query" or "object (optional) - params"
				let schemaType = 'string';
				const typeMatch = valueStr.match(/^(string|number|integer|boolean|array|object)\b/);
				if (typeMatch) {
					schemaType = typeMatch[1];
				}

				const isOptional = valueStr.includes('(optional)') || valueStr.includes('optional');
				const description = typeof typeOrValue === 'string' ? typeOrValue : `Parameter: ${key}`;

				if (schemaType === 'array') {
					properties[key] = { type: 'array', items: { type: 'string' }, description };
				} else if (schemaType === 'object') {
					properties[key] = { type: 'object', description };
				} else {
					properties[key] = { type: schemaType, description };
				}

				if (!isOptional) {
					required.push(key);
				}
			});

			return {
				name: tool.id,
				description: tool.description,
				input_schema: {
					type: 'object',
					properties,
					required: required.length > 0 ? required : undefined,
				},
			};
		});
		llmTools.push(RESOLVE_QUERY_ID_TOOL);

		// Tool handler to execute external tools
		const toolHandler = async (toolName: string, toolInput: any): Promise<string> => {
			if (toolName === RESOLVE_QUERY_ID_TOOL.name) {
				logger.info(`[DASH_COMP_REQ] Resolving ${toolInput?.queryIds?.length ?? 0} queryId token(s)`);
				return resolveQueryIdsTool(toolInput?.queryIds, collections);
			}

			const tool = tools?.find(t => t.id === toolName);
			if (!tool) {
				throw new Error(`Unknown tool: ${toolName}`);
			}

			logger.info(`[DASH_COMP_REQ] Executing external tool: ${tool.name} (${tool.id})`);
			const result = await tool.fn(toolInput);

			executedTools.push({
				id: tool.id,
				name: tool.name,
				params: toolInput,
				result: result,
				outputSchema: tool.outputSchema
			});

			logger.info(`[DASH_COMP_REQ] Tool ${tool.name} executed successfully`);
			const resultJson = JSON.stringify(result, null, 2);
			return resultJson + '\n\n[REMINDER: The above is tool output for verification. You MUST still respond with ONLY the JSON component selection object. Do NOT summarize or describe these results.]';
		};

		// Make LLM call with tool support
		const result = await LLM.streamWithTools(
			{
				sys: prompts.system,
				user: prompts.user
			},
			llmTools,
			toolHandler,
			{
				model,
				maxTokens: 8192,
				temperature: 0.2,
				apiKey
			},
			5 // max iterations
		);

		// Parse the JSON response
		let jsonMatch = result.match(/\{[\s\S]*\}/);
		let parsedResult = jsonMatch ? (() => { try { return JSON.parse(jsonMatch![0]); } catch { return null; } })() : null;

		// Validate it has the required component structure
		const isValidComponent = parsedResult && parsedResult.componentId && parsedResult.props;

		// If first attempt failed (prose response or invalid JSON), retry with tool data baked in
		if (!isValidComponent && executedTools.length > 0) {
			const toolDataSummary = executedTools.map(t =>
				`Tool "${t.name}" was called with params ${JSON.stringify(t.params)} and returned:\n${JSON.stringify(t.result, null, 2).substring(0, 5000)}`
			).join('\n\n');

			const retryUserPrompt = `Original user request: ${prompt}\n\nThe following tool was already called and returned data:\n${toolDataSummary}\n\nUsing this data, select the appropriate component and respond with ONLY the JSON component selection object. No explanation, no markdown, just JSON.`;

			const retryResult = await LLM.text(
				{
					sys: prompts.system,
					user: retryUserPrompt
				},
				{
					model,
					maxTokens: 4096,
					temperature: 0.1,
					apiKey
				}
			);

			jsonMatch = retryResult.match(/\{[\s\S]*\}/);
			parsedResult = jsonMatch ? (() => { try { return JSON.parse(jsonMatch![0]); } catch { return null; } })() : null;
		}

		if (!parsedResult) {
			errors.push('Failed to parse LLM response as JSON');
			errors.push(`LLM Response: ${result}`);
			logger.error(`[DASH_COMP_REQ] Failed to parse JSON from LLM response`);
			return { success: false, errors };
		}

		logger.info(`[DASH_COMP_REQ] Parsed component: ${parsedResult.componentName} (${parsedResult.componentId})`);
		logger.file('[DASH_COMP_REQ] LLM response:', JSON.stringify(parsedResult, null, 2));

		// Validate response structure
		if (!parsedResult.componentId || !parsedResult.props) {
			errors.push('Invalid LLM response: missing componentId or props');
			errors.push(`LLM Response: ${result}`);
			logger.error(`[DASH_COMP_REQ] Invalid structure - missing componentId: ${!parsedResult.componentId}, missing props: ${!parsedResult.props}`);
			userPromptErrorLogger.logError('DASH_COMP_REQ', 'Invalid LLM response structure', {
				prompt,
				result: parsedResult,
				missingFields: { componentId: !parsedResult.componentId, props: !parsedResult.props }
			});
			return { success: false, errors };
		}

		// Find the original component
		const originalComponent = components.find(c => c.name === parsedResult.componentName);
		if (!originalComponent) {
			errors.push(`Component ${parsedResult.componentName} not found in available components`);
			errors.push(`LLM Response: ${result}`);
			userPromptErrorLogger.logError('DASH_COMP_REQ', 'Component not found', {
				prompt,
				componentName: parsedResult.componentName,
				availableComponentNames: components.map(c => c.name)
			});
			return { success: false, errors };
		}

		// Build the final component with generated props
		let finalComponent: Component = {
			...originalComponent,
			props: {
				...originalComponent.props,
				...parsedResult.props
			}
		};

		// Ensure query limit before validation
		if (finalComponent.props?.query) {
			const query = finalComponent.props.query as string | { sql: string; values?: any; params?: any };
			if (typeof query === 'string') {
				finalComponent.props.query = ensureQueryLimit(query, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT);
			} else if (query?.sql) {
				finalComponent.props.query = {
					...query,
					sql: ensureQueryLimit(query.sql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT)
				};
			}
		}

		// Ensure query limit for externalTool SQL parameters
		const etSql = (finalComponent.props as any)?.externalTool?.parameters?.sql;
		if (etSql && typeof etSql === 'string') {
			const etToolId = (finalComponent.props as any)?.externalTool?.toolId;
			const etDbType = getDbTypeFromToolId(etToolId);
			(finalComponent.props as any).externalTool.parameters.sql = ensureQueryLimit(etSql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, etDbType);
		}

		logger.info(`[DASH_COMP_REQ] Successfully picked component: ${finalComponent.name} (${finalComponent.type})`);

		// Validate direct query prop if present (legacy path)
		if (finalComponent.props?.query && collections?.['database']?.['execute']) {
			logger.info(`[DASH_COMP_REQ] Validating direct query for component: ${finalComponent.name}`);

			const queryService = new QueryExecutionService({
				defaultLimit: DEFAULT_QUERY_LIMIT,
				getModelForTask: () => model,
				getApiKey: () => apiKey,
				providerName: 'DASH_COMP_REQ'
			});

			try {
				const validationResult = await queryService.validateSingleQuery(
					finalComponent,
					collections,
					apiKey
				);

				if (!validationResult.validated) {
					logger.error(`[DASH_COMP_REQ] Query validation failed for component: ${finalComponent.name}`);
					errors.push(`Query validation failed for component ${finalComponent.name}. The generated SQL query could not be executed.`);
					return {
						success: false,
						errors,
						data: {
							reasoning: parsedResult.reasoning || 'Component selected but query validation failed',
							rawResponse: parsedResult
						}
					};
				}

				if (validationResult.component) {
					finalComponent = validationResult.component;
				}

				logger.info(`[DASH_COMP_REQ] Query validated successfully for component: ${finalComponent.name}`);
			} catch (validationError) {
				const validationErrorMsg = validationError instanceof Error ? validationError.message : String(validationError);
				logger.error(`[DASH_COMP_REQ] Query validation error: ${validationErrorMsg}`);
				errors.push(`Query validation error: ${validationErrorMsg}`);
				return {
					success: false,
					errors,
					data: {
						reasoning: parsedResult.reasoning || 'Component selected but query validation encountered an error',
						rawResponse: parsedResult
					}
				};
			}
		} else if (finalComponent.props?.query && !collections?.['database']?.['execute']) {
			logger.warn(`[DASH_COMP_REQ] Skipping query validation - database execute function not available`);
		}

		// Validate externalTool SQL via external-tools.execute with retry + LLM fix
		const extToolId = (finalComponent.props as any)?.externalTool?.toolId;
		const extToolName = (finalComponent.props as any)?.externalTool?.toolName;
		let extToolSql = (finalComponent.props as any)?.externalTool?.parameters?.sql;
		if (extToolSql && extToolId && collections?.['external-tools']?.['execute']) {
			logger.info(`[DASH_COMP_REQ] Validating externalTool SQL for component: ${finalComponent.name}`);

			// Find the tool to get schema for LLM fix
			const matchedTool = tools?.find(t => t.id === extToolId);
			let validated = false;
			let attempts = 0;

			// The component's own default param VALUES (e.g. { startDate: '2025-04-01' })
			// for its $paramName placeholders. Without forwarding these, every query
			// using named params with defaults fails validation with "Invalid
			// pseudocolumn" (MSSQL) since $startDate is never bound to anything —
			// create-filter.ts's equivalent validation already does this correctly;
			// this brings pick-component.ts's single-component path in line with it.
			const defaultParams = (finalComponent.props as any)?.externalTool?.parameters?.params || {};

			while (attempts < MAX_QUERY_VALIDATION_RETRIES && !validated) {
				attempts++;
				try {
					const result = await collections['external-tools']['execute']({
						toolId: extToolId, toolName: extToolName, sql: extToolSql, ...defaultParams, data: {},
					});

					if (result?.success === false || result?.error) {
						const errorMsg = result?.error || 'Unknown error';
						throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
					}

					// Success — update component with (possibly fixed) SQL and cache the result
					(finalComponent.props as any).externalTool.parameters.sql = extToolSql;
					validated = true;
					const cacheKey = extToolId ? `${extToolId}:${extToolSql}` : extToolSql;
					queryCache.set(cacheKey, result?.data ?? result);
					logger.info(`[DASH_COMP_REQ] externalTool SQL validated (attempt ${attempts}) - cached for frontend`);
				} catch (execError) {
					const errorMsg = execError instanceof Error ? execError.message : String(execError);
					logger.warn(`[DASH_COMP_REQ] externalTool SQL failed (attempt ${attempts}/${MAX_QUERY_VALIDATION_RETRIES}): ${errorMsg}`);

					if (attempts >= MAX_QUERY_VALIDATION_RETRIES) {
						logger.error(`[DASH_COMP_REQ] Max retries reached for externalTool SQL validation`);
						errors.push(`SQL validation failed for component ${finalComponent.name}: ${errorMsg}`);
						return {
							success: false,
							errors,
							data: {
								reasoning: parsedResult.reasoning || 'Component selected but externalTool SQL validation failed after retries',
								rawResponse: parsedResult
							}
						};
					}

					// Ask LLM to fix the SQL
					try {
						const toolSchema = matchedTool?.description || 'Schema not available';
						const databaseRules = await promptLoader.loadDatabaseRulesForType(getDbTypeFromToolId(extToolId));

						const fixPrompt = `You are a SQL expert. Fix the following SQL query that failed execution.

## Database Schema
${toolSchema}

## Database-Specific SQL Rules
${databaseRules}

## Component Context
- Component Name: ${finalComponent.name}
- Component Type: ${finalComponent.type}
- Title: ${(finalComponent.props as any)?.title || 'N/A'}

## Failed Query
\`\`\`sql
${extToolSql}
\`\`\`

## Error Message
${errorMsg}

## Instructions
1. Analyze the error message and identify what caused the query to fail
2. Fix the query to resolve the error while preserving the original intent
3. Ensure the fixed query follows the database-specific SQL rules above
4. Return ONLY the fixed SQL query, no explanations or markdown

Fixed SQL query:`;

						const fixResponse = await LLM.text(
							{
								sys: 'You are a SQL expert. Return only the fixed SQL query with no additional text, explanations, or markdown formatting.',
								user: fixPrompt,
							},
							{
								model: model || undefined,
								maxTokens: 2048,
								temperature: 0,
								apiKey,
							}
						);

						let fixedSql = fixResponse.trim();
						fixedSql = fixedSql.replace(/^```sql\s*/i, '').replace(/\s*```$/i, '');
						fixedSql = fixedSql.replace(/^```\s*/i, '').replace(/\s*```$/i, '');

						const { query: validatedSql } = validateAndFixSqlQuery(fixedSql);

						if (validatedSql && validatedSql !== extToolSql) {
							extToolSql = ensureQueryLimit(validatedSql, DEFAULT_QUERY_LIMIT, MAX_COMPONENT_QUERY_LIMIT, getDbTypeFromToolId(extToolId));
							logger.info(`[DASH_COMP_REQ] LLM provided SQL fix, retrying...`);
						} else {
							logger.warn(`[DASH_COMP_REQ] LLM returned same or empty query, stopping retries`);
							errors.push(`SQL validation failed for component ${finalComponent.name}: ${errorMsg}`);
							return {
								success: false,
								errors,
								data: {
									reasoning: parsedResult.reasoning || 'Component selected but SQL fix failed',
									rawResponse: parsedResult
								}
							};
						}
					} catch (fixError) {
						const fixMsg = fixError instanceof Error ? fixError.message : String(fixError);
						logger.error(`[DASH_COMP_REQ] Failed to get LLM SQL fix: ${fixMsg}`);
						errors.push(`SQL validation failed for component ${finalComponent.name}: ${errorMsg}`);
						return {
							success: false,
							errors,
							data: {
								reasoning: parsedResult.reasoning || 'Component selected but SQL fix encountered an error',
								rawResponse: parsedResult
							}
						};
					}
				}
			}
		}

		// Replace SQL with queryId — only queryIds sent to frontend (same as agent flow)
		const props: any = { ...finalComponent.props };

		// Handle externalTool.parameters.sql → replace with queryId
		const sqlValue = props.externalTool?.parameters?.sql || props.externalTool?.parameters?.query;
		if (sqlValue) {
			const { sql, query, ...restParams } = props.externalTool.parameters;
			const toolIdForCache = props.externalTool?.toolId || '';
			const cacheKeyForGet = toolIdForCache ? `${toolIdForCache}:${sqlValue}` : sqlValue;
			const cachedData = queryCache.get(cacheKeyForGet);
			const queryId = queryCache.storeQuery(sqlValue, cachedData);
			props.externalTool = {
				...props.externalTool,
				parameters: { queryId, ...restParams },
			};
			logger.info(`[DASH_COMP_REQ] Replaced externalTool SQL with queryId: ${queryId}`);
		}

		// Handle legacy direct props.query → replace with queryId
		if (props.query) {
			const { query, ...restProps } = props;
			const queryId = queryCache.storeQuery(query);
			Object.keys(props).forEach(k => delete props[k]);
			Object.assign(props, { ...restProps, queryId });
			logger.info(`[DASH_COMP_REQ] Replaced direct query with queryId: ${queryId}`);
		}

		finalComponent = { ...finalComponent, props };

		// Pre-populate the et-direct cache slot for direct tools so the frontend's
		// first render hits cache instead of re-executing the tool. SQL tools have
		// the queryId substitution above; direct tools key on the (toolId, params)
		// pair the frontend will dispatch, which is reproducible without a token.
		const ext = (finalComponent.props as any)?.externalTool;
		if (ext?.toolId && !ext?.parameters?.sql) {
			const matchedTool = tools?.find(t => t.id === ext.toolId);
			if (matchedTool && matchedTool.cache !== false) {
				const match = executedTools.find(t => t.id === ext.toolId);
				if (match) {
					const cacheKey = buildDirectToolCacheKey(ext.toolId, ext.parameters);
					queryCache.set(cacheKey, { success: true, data: match.result });
					logger.info(`[DASH_COMP_REQ] Pre-populated et-direct cache for ${ext.toolId}`);
				}
			}
		}

		// Log data source info
		if (parsedResult.props.query) {
			logger.info(`[DASH_COMP_REQ] Data source: Database query`);
		}
		if (parsedResult.props.externalTool) {
			logger.info(`[DASH_COMP_REQ] Data source: External tool - ${parsedResult.props.externalTool.toolName}`);
		}
		if (executedTools.length > 0) {
			logger.info(`[DASH_COMP_REQ] Executed ${executedTools.length} external tool(s): ${executedTools.map(t => t.name).join(', ')}`);
		}

		return {
			success: true,
			data: {
				component: finalComponent,
				reasoning: parsedResult.reasoning || 'Component selected based on user prompt',
				dataSource: parsedResult.props.query ? 'database' : (parsedResult.props.externalTool ? 'external_tool' : 'none'),
				isUpdate: parsedResult.isUpdate || false,
				executedTools: executedTools.length > 0 ? executedTools : undefined
			},
			errors: []
		};
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		logger.error(`[DASH_COMP_REQ] Error picking component: ${errorMsg}`);

		// Log error with full details
		userPromptErrorLogger.logError('DASH_COMP_REQ', error instanceof Error ? error : new Error(errorMsg), {
			prompt,
			componentsCount: components.length,
			toolsCount: tools?.length || 0
		});

		errors.push(errorMsg);
		return { success: false, errors };
	}
}
